
<script setup>
import { useRuntimeConfig } from '#imports';
import { useAuthentication } from '@/composables/use-authentication';
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import useSnackbar from '@/components/commons/global-snack/composables';

const { show: showSnackbar } = useSnackbar();

const { currentUser, signout } = useAuthentication();
const route = useRoute();
const routeName = computed(() => route.name);

const picURL = computed(() => currentUser.value?.picURL);
const formattedName = computed(() => {
  return currentUser.value?.formattedName;
});
const email = computed(() => {
  return currentUser.value?.email;
});

async function onLogout () {
  await signout();
  showSnackbar('You have been successfully logged out.', { type: 'success' });
  setTimeout(() => {
    globalThis.location.href = '/';
  }, 500);
};

function onClickDoBlur () {
  if (routeName.value === 'settings-account') return;
  globalThis.document.getElementById('menuProfileItemRef').blur();
}

const appVersion = useRuntimeConfig().public?.appVersion;
</script>

<template>
<div class="dropdown dropdown-end">
  <!-- User Avatar with Modern Styling -->
  <div 
    class="avatar mr-2 hover:cursor-pointer group" 
    tabindex="0"
  >
    <div
      class="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-red-600 ring-2 ring-transparent group-hover:ring-gray-200 transition-all duration-200 shadow-sm group-hover:shadow-md"
      :class="{
        'ring-primary/30 shadow-lg': routeName === 'settings-account',
      }"
    >
      <img 
        v-if="picURL" 
        :src="picURL" 
        class="w-full h-full rounded-xl object-cover"
      />
      <div v-else class="flex items-center justify-center h-full">
        <i class="las la-user text-white text-xl"></i>
      </div>
    </div>
  </div>
  
  <!-- Enhanced Dropdown Menu -->
  <ul 
    tabindex="0" 
    class="dropdown-content z-[1] menu p-0 shadow-xl bg-white rounded-2xl border border-gray-100 mt-2 overflow-hidden"
  >
    <!-- User Profile Header -->
    <li class="bg-gradient-to-r from-gray-50 to-gray-100/50">
      <nuxt-link 
        id="menuProfileItemRef" 
        class="flex items-center gap-4 p-4 hover:bg-gradient-to-r hover:from-primary/5 hover:to-red-50 transition-all duration-200 border-b border-gray-100" 
        to="/settings/account/personal" 
        @click="onClickDoBlur"
      >
        <!-- Mini Avatar -->
        <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-red-600 flex items-center justify-center flex-shrink-0 shadow-sm">
          <img 
            v-if="picURL" 
            :src="picURL" 
            class="w-full h-full rounded-xl object-cover"
          />
          <i v-else class="las la-user text-white text-lg"></i>
        </div>
        
        <!-- User Info -->
        <div class="flex-1 min-w-0">
          <div class="font-semibold text-gray-900 truncate">{{ formattedName }}</div>
          <div class="text-sm text-gray-600 truncate">{{ email }}</div>
          <div class="text-xs text-gray-500 mt-1 flex items-center gap-1">
            <i class="las la-user-cog text-lg"></i>
            View Profile
          </div>
        </div>
        
        <i class="las la-arrow-right text-gray-400 text-lg"></i>
      </nuxt-link>
    </li>
    
    <!-- Menu Actions -->
    <div class="p-2">
      <!-- Settings Link -->
      <li>
        <nuxt-link 
          to="/settings/account/personal" 
          class="flex items-center gap-3 p-3 rounded-xl hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 transition-all duration-200 text-gray-700 hover:text-gray-900"
        >
          <div class="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
            <i class="las la-cog text-gray-600 text-lg"></i>
          </div>
          <span class="font-medium">Settings</span>
        </nuxt-link>
      </li>
      
      <!-- Logout -->
      <li>
        <a 
          @click="onLogout"
          class="flex items-center gap-3 p-3 rounded-xl hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 transition-all duration-200 text-gray-700 hover:text-red-600 cursor-pointer"
        >
          <div class="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
            <i class="las la-sign-out-alt text-red-600 text-lg"></i>
          </div>
          <span class="font-medium">Log out</span>
        </a>
      </li>
    </div>
    
    <!-- App Version Footer -->
    <div class="px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100/50 border-t border-gray-100">
      <div class="text-xs text-gray-500 flex items-center justify-between">
        <span>App Version: {{ appVersion }}</span>
        <div class="flex items-center gap-1 text-green-600">
          <div class="w-2 h-2 bg-green-500 rounded-full"></div>
          <span>Online</span>
        </div>
      </div>
    </div>
  </ul>
</div>
</template>